import { IntlShape } from 'react-intl';

import { Action, ActionType, FileStatus, WidgetCustomizationState } from './types';

export const initialState: WidgetCustomizationState = {
  fetching: true,
  submitting: false,
  isPreview: true,
  submitError: '',
  widgetPreviewSettings: {
    languageOptions: [],
    countryOptions: [],
    language: 'en',
    country: 'XX'
  },
  oauth2: {
    oauth2Options: [],
    currOAuth2: {
      name: '',
      access: [],
      project_id: '',
      redirect_uris: [],
      secret: '',
      response_types: [],
      id: null,
      is_public: false,
      grant_types: [],
      lifetime: 0
    },
    oauth2Id: '',
    allOAuth2: []
  },
  visualSettings: {
    customStyleUrl: '',
    pristine: true
  },
  oldLoginWidget: {
    widgetStyle: null,
    uploadStatus: FileStatus.IDLE_STATUS,
    uploadFileValue: null
  },
  isAgreementSigned: false,
  intl: null
};

export const init = (isAgreementSigned: boolean, intl: IntlShape): WidgetCustomizationState => {
  return {
    ...initialState,
    intl,
    isAgreementSigned
  };
};

export const widgetCustomizationReducer = (
  state: WidgetCustomizationState,
  action: Action
): WidgetCustomizationState => {
  const { intl } = state;

  if (!intl) {
    return initialState;
  }

  switch (action.type) {
    case ActionType.setFetching:
      return { ...state, fetching: action.payload };
    case ActionType.setCountryOptions:
      return {
        ...state,
        widgetPreviewSettings: {
          ...state.widgetPreviewSettings,
          countryOptions: action.payload
            .map((currCountry) => ({
              value: currCountry,
              children: intl.formatMessage({
                id:
                  currCountry === 'XX'
                    ? 'widget-customization.settings.placeholder.autodetected'
                    : `form.select.country-name.${currCountry}`
              })
            }))
            .sort((a, b) => {
              if (a.value === 'XX') {
                return -1;
              }

              return a.children.localeCompare(b.children);
            })
        }
      };
    case ActionType.setLanguageOptions:
      return {
        ...state,
        widgetPreviewSettings: {
          ...state.widgetPreviewSettings,
          languageOptions: action.payload
            .map((currLanguage) => ({
              value: currLanguage,
              children: intl.formatMessage({
                id: `widget-customization.settings.language-select.language.${currLanguage}`
              })
            }))
            .sort((a, b) => a.children.localeCompare(b.children))
        }
      };
    case ActionType.setOauth2Options:
      return {
        ...state,
        oauth2: {
          ...state.oauth2,
          allOAuth2: action.payload,
          oauth2Options: action.payload.reduce(
            (accumulator, currOauth2) => [
              ...accumulator,
              {
                value: `${currOauth2.id}`,
                children: `${currOauth2.name} [${currOauth2.id}]`
              }
            ],
            [
              {
                value: '',
                children: intl.formatMessage({
                  id: 'widget-customization.settings.oauth.dropdown.none'
                })
              }
            ]
          )
        }
      };
    case ActionType.setLanguage:
      if (action.payload === 'cn') {
        return {
          ...state,
          widgetPreviewSettings: {
            ...state.widgetPreviewSettings,
            language: action.payload,
            country: 'CN'
          }
        };
      }
      if (action.payload === 'tw') {
        return {
          ...state,
          widgetPreviewSettings: {
            ...state.widgetPreviewSettings,
            language: action.payload,
            country: 'TW'
          }
        };
      }
      return {
        ...state,
        widgetPreviewSettings: { ...state.widgetPreviewSettings, language: action.payload }
      };
    case ActionType.setCountry:
      return {
        ...state,
        widgetPreviewSettings: { ...state.widgetPreviewSettings, country: action.payload }
      };
    case ActionType.setOauth2Id:
      return {
        ...state,
        oauth2: {
          ...state.oauth2,
          currOAuth2:
            state.oauth2.allOAuth2.find((element) => element.id === Number(action.payload)) || null,
          oauth2Id: action.payload
        }
      };
    case ActionType.setCustomStyleUrl:
      return {
        ...state,
        visualSettings: { ...state.visualSettings, customStyleUrl: action.payload, pristine: false }
      };
    case ActionType.setOldWidgetStyle:
      return {
        ...state,
        oldLoginWidget: { ...state.oldLoginWidget, widgetStyle: action.payload }
      };
    case ActionType.setOldWidgetFileStatus:
      return {
        ...state,
        oldLoginWidget: { ...state.oldLoginWidget, uploadStatus: action.payload }
      };
    case ActionType.setOldWidgetFileValue:
      return {
        ...state,
        oldLoginWidget: { ...state.oldLoginWidget, uploadFileValue: action.payload }
      };
    case ActionType.togglePreview:
      return { ...state, isPreview: !state.isPreview };
    case ActionType.submitStarted:
      return { ...state, submitting: true, submitError: '' };
    case ActionType.submitSuccess:
      return {
        ...state,
        submitting: false
      };
    case ActionType.submitFailed:
      return {
        ...state,
        submitting: false,
        submitError: action.payload
      };
    default:
      throw new Error('Unknown action type in Widget Customization reducer!');
  }
};
